import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';

/**
 * Shown when the backend returns 429 Too Many Requests. Reads the
 * Retry-After header (in seconds) from the failed response and runs a
 * countdown so the dispatcher knows exactly when they can try again.
 *
 * Style matches the existing app modals (Magic Loading, Custom Flight,
 * Note for All Drivers) — frosted dark panel, soft border, amber accent
 * for the warning state.
 */
function RateLimitDialog({ open, retryAfterSeconds, onClose }) {
  const [remaining, setRemaining] = useState(retryAfterSeconds);
  // `initial` must reset every time the dialog opens — this component stays
  // mounted (open is just toggled), so a plain useState(retryAfterSeconds)
  // would freeze at the mount-time value (0) and the progress bar would never
  // move. Reset it alongside `remaining` on open.
  const [initial, setInitial] = useState(retryAfterSeconds);

  useEffect(() => {
    if (!open) return;
    setRemaining(retryAfterSeconds);
    setInitial(retryAfterSeconds);
  }, [open, retryAfterSeconds]);

  useEffect(() => {
    if (!open || remaining <= 0) return;
    const id = setInterval(() => {
      setRemaining((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [open, remaining]);

  const ready = remaining <= 0;
  const progress = initial > 0
    ? Math.max(0, Math.min(100, ((initial - remaining) / initial) * 100))
    : 100;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={ready ? onClose : undefined}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(31, 41, 55, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 152, 0, 0.4)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          color: '#fff',
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } },
      }}
    >
      <DialogTitle
        sx={{
          color: '#FF9800',
          backgroundColor: 'transparent',
          textAlign: 'center',
          fontWeight: 'bold',
        }}
      >
        Server is asking us to slow down
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: 'transparent',
          textAlign: 'center',
          pt: 1,
        }}
      >
        <Typography
          sx={{ color: 'rgba(255, 255, 255, 0.85)', mb: 2 }}
        >
          Too many requests in the last minute.
        </Typography>
        <Box sx={{ my: 2 }}>
          {ready ? (
            <Typography
              sx={{
                fontSize: '1.4rem',
                fontWeight: 'bold',
                color: '#4CAF50',
              }}
            >
              You can try again now.
            </Typography>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: '2.5rem',
                  fontWeight: 'bold',
                  color: '#FF9800',
                  lineHeight: 1,
                }}
              >
                {remaining}s
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1 }}
              >
                You'll be able to try again automatically.
              </Typography>
            </>
          )}
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mt: 2,
            height: 6,
            borderRadius: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: ready ? '#4CAF50' : '#FF9800',
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={!ready}
          variant="contained"
          sx={{
            minWidth: '120px',
            backgroundColor: ready ? '#4CAF50' : 'rgba(255, 255, 255, 0.12)',
            color: ready ? '#fff' : 'rgba(255, 255, 255, 0.4)',
            '&:hover': {
              backgroundColor: ready ? '#45a049' : 'rgba(255, 255, 255, 0.12)',
            },
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RateLimitDialog;
